/**
 * MembershipHistory Component (ORCH-336, ORCH-21 §10.5, ORCH-422; PRD User Manager UM-12)
 *
 * Purpose: Every audited change on the user card in one list, newest first, one
 *          entry per save: who made it and when, then one line per change it
 *          made. Profile fields read "Country: Not set → France"; access changes
 *          (roles, security attributes, Company, status) show the values before
 *          and after. Invitation events (sent, resent, cancelled, the email's
 *          outcome, ORCH-420) are one line each under Access. A Profile / Access
 *          filter narrows a long list to the lines of that kind.
 * Dependencies: userManagementService.getHistory, useMembershipFields (names of
 *               the membership's fields and, via `profileNames`, the profile's)
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import userManagementService, { userErrorMessage } from '../../services/userManagementService';
import { MembershipHistoryChange, MembershipHistoryEntry } from '../../types/userManagement';
import { formatAuditTimestamp, formatAuditUser } from '../../utils/formatting';
import { asList } from '../../utils/listValues';
import { auditPerson, describeProfileValue } from './membership';

/** The review step (ORCH-410) and History word a profile value the same way. */
export { describeProfileValue };

const ACTION_LABELS: Record<string, string> = {
  'membership.created': 'Added',
  'membership.roles_changed': 'Roles',
  'membership.attributes_changed': 'Organization and Security Attributes',
  'membership.status_changed': 'Status',
  'membership.removed': 'Removed',
  'account.status_changed': 'Account',
};

// Account events with no before and after: one line each (ORCH-296). They are
// about the person's account, so they show under the Profile filter.
const EVENT_LABELS: Record<string, string> = {
  'account.password_reset_sent': 'Password reset email sent',
  'account.password_reset_completed': 'Password changed by the user',
};

// Invitation events (ORCH-420): one line each, under Access
const INVITE_LABELS: Record<string, (details: InviteDetails) => string> = {
  'invite.created': () => 'Invitation sent',
  'invite.resent': () => 'Invitation resent',
  'invite.cancelled': () => 'Invitation cancelled',
  'invite.email': (details) =>
    details.status === 'sent'
      ? 'Invitation email sent'
      : `Invitation email not sent${details.error ? `: ${details.error}` : ''}`,
};

interface InviteDetails {
  status?: string;
  error?: string | null;
  expires_at?: string | null;
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  inactive: 'Deactivated',
  invited: 'Invited',
  pending: 'Invited',
};

export interface MembershipHistoryProps {
  userId: string;
  tenantId?: string;
  fieldLabel: (fielddefId: string) => string;
  valueName: (fielddefId: string, valueId: string) => string;
  /** Labels and value names of the profile (`user`) form, for profile entries. */
  profileNames?: Names;
}

interface Names {
  fieldLabel: (fielddefId: string) => string;
  valueName: (fielddefId: string, valueId: string) => string;
}

type Filter = 'all' | 'profile' | 'access';

const isProfile = (change: MembershipHistoryChange): boolean =>
  change.kind === 'profile' || change.action in EVENT_LABELS;

const inviteDetails = (change: MembershipHistoryChange): InviteDetails => change.details || {};

const roleNames = (roles: unknown): string =>
  (Array.isArray(roles) ? roles : [])
    .map((r) => (r as { name?: string }).name)
    .filter(Boolean)
    .join(', ') || 'None';

const attributeText = (metadata: unknown, names: Names): string =>
  Object.entries((metadata as Record<string, unknown>) || {})
    .map(([fielddefId, value]) => {
      const values = asList(value).map((id) => names.valueName(fielddefId, id));
      return values.length ? `${names.fieldLabel(fielddefId)}: ${values.join(', ')}` : null;
    })
    .filter(Boolean)
    .join(' · ') || 'None';

/** One side of a change, in words. */
export const describeState = (action: string, state: unknown, names: Names): string => {
  if (state === null || state === undefined) return '—';
  if (action === 'membership.roles_changed') return roleNames(state);
  if (action === 'membership.attributes_changed') return attributeText(state, names);
  if (action === 'membership.status_changed' || action === 'account.status_changed')
    return STATUS_LABELS[String(state)] || String(state);
  const snapshot = state as { status?: string; roles?: unknown; metadata?: unknown };
  return [
    snapshot.status ? STATUS_LABELS[snapshot.status] || snapshot.status : null,
    `Roles: ${roleNames(snapshot.roles)}`,
    attributeText(snapshot.metadata, names),
  ]
    .filter(Boolean)
    .join(' · ');
};

/**
 * The entries to show for a filter: every entry with a change of that kind, listing
 * only those changes. One save stays one entry; the other kind's lines are left out.
 */
export const filterEntries = (
  entries: MembershipHistoryEntry[],
  filter: Filter
): MembershipHistoryEntry[] =>
  filter === 'all'
    ? entries
    : entries
        .map((entry) => ({
          ...entry,
          changes: entry.changes.filter((change) => (filter === 'profile') === isProfile(change)),
        }))
        .filter((entry) => entry.changes.length > 0);

const MembershipHistory: React.FC<MembershipHistoryProps> = ({
  userId,
  tenantId,
  fieldLabel,
  valueName,
  profileNames = { fieldLabel, valueName },
}) => {
  const [entries, setEntries] = useState<MembershipHistoryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');

  useEffect(() => {
    let current = true;
    setEntries(null);
    setError(null);
    userManagementService
      .getHistory(userId, tenantId)
      .then((data) => current && setEntries(data))
      .catch((e) => current && setError(userErrorMessage(e, 'History could not be loaded.')));
    return () => {
      current = false;
    };
  }, [userId, tenantId]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (entries === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const shown = filterEntries(entries, filter);
  const accessNames = { fieldLabel, valueName };

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="membership-history">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Changes
        </Typography>
        {entries.length > 0 && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={filter}
            onChange={(_e, next: Filter | null) => next && setFilter(next)}
            aria-label="Show changes"
            data-testid="history-filter"
          >
            <ToggleButton value="all" data-testid="history-filter-all">
              All
            </ToggleButton>
            <ToggleButton value="profile" data-testid="history-filter-profile">
              Profile
            </ToggleButton>
            <ToggleButton value="access" data-testid="history-filter-access">
              Access
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>
      {shown.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No changes recorded yet.
        </Typography>
      )}
      {shown.map((entry) => (
        <Box
          key={entry.change_id}
          data-testid="history-event"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '200px minmax(0, 1fr)' },
            gap: 1.5,
            py: 1.25,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Box>
            <Typography variant="body2">
              {entry.timestamp ? formatAuditTimestamp(entry.timestamp) : '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatAuditUser(entry.actor_id, auditPerson(entry.actor))}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {entry.changes.map((change) =>
              change.action in EVENT_LABELS ? (
                <Typography key={change.log_id} variant="body2" data-testid="history-change">
                  <Box component="span" fontWeight={500}>
                    {EVENT_LABELS[change.action]}
                  </Box>
                </Typography>
              ) : change.action in INVITE_LABELS ? (
                <Typography key={change.log_id} variant="body2" data-testid="history-change">
                  <Box component="span" fontWeight={500}>
                    {INVITE_LABELS[change.action](inviteDetails(change))}
                  </Box>
                </Typography>
              ) : isProfile(change) && change.field ? (
                <Typography key={change.log_id} variant="body2" data-testid="history-change">
                  <Box component="span" fontWeight={500}>
                    {profileNames.fieldLabel(change.field)}:
                  </Box>{' '}
                  {describeProfileValue(change.field, change.before, profileNames)} →{' '}
                  {describeProfileValue(change.field, change.after, profileNames)}
                </Typography>
              ) : (
                <Box key={change.log_id} data-testid="history-change">
                  <Typography variant="body2" fontWeight={500}>
                    {ACTION_LABELS[change.action] || change.action}
                  </Typography>
                  <Typography variant="body2" color="error.main">
                    − {describeState(change.action, change.before, accessNames)}
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    + {describeState(change.action, change.after, accessNames)}
                  </Typography>
                </Box>
              )
            )}
          </Box>
        </Box>
      ))}
    </Paper>
  );
};

export default MembershipHistory;
